import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CANCELED_BEFORE_CONNECT, softphoneService } from './service';
import { useSoftphone } from '../store/softphone';
import { api } from '../lib/api';
import { extractApiErrorMessage } from '../lib/api-error';
import { normalizePhoneToE164, isValidUKPhoneNumber } from '@marnic/shared';
import type { Lead, TwilioNumber } from '@marnic/shared';

const KEYS: Array<{ digit: string; letters?: string }> = [
  { digit: '1' }, { digit: '2', letters: 'ABC' }, { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' }, { digit: '5', letters: 'JKL' }, { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' }, { digit: '8', letters: 'TUV' }, { digit: '9', letters: 'WXYZ' },
  { digit: '*' }, { digit: '0', letters: '+' }, { digit: '#' },
];

function normalizeProvider(provider: unknown): 'TWILIO' | 'VOIPLINE' {
  return typeof provider === 'string' && provider.trim().toUpperCase() === 'VOIPLINE'
    ? 'VOIPLINE'
    : 'TWILIO';
}

export function DialPad(): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const prefillKeyRef = useRef<string | null>(null);
  const number = useSoftphone((s) => s.callDraftNumber);
  const selectedCallerId = useSoftphone((s) => s.selectedCallerId);
  const selectedCallerProvider = useSoftphone((s) => s.selectedCallerProvider);
  const setLead = useSoftphone((s) => s.setLead);
  const setNumber = useSoftphone((s) => s.setCallDraftNumber);
  const setSelectedCallerId = useSoftphone((s) => s.setSelectedCallerId);

  const numbersQuery = useQuery({
    queryKey: ['twilio-numbers'],
    queryFn: () => api.get<(TwilioNumber & { provider?: string })[]>('/twilio-numbers'),
  });

  useEffect(() => {
    if (!selectedCallerId || selectedCallerProvider || !numbersQuery.data) return;
    const selectedNumber = numbersQuery.data.find((n) => n.phoneE164 === selectedCallerId);
    if (selectedNumber) {
      setSelectedCallerId(selectedCallerId, normalizeProvider(selectedNumber.provider));
    }
  }, [numbersQuery.data, selectedCallerId, selectedCallerProvider, setSelectedCallerId]);

  useEffect(() => {
    const perfexLeadId = searchParams.get('leadId') ?? '';
    const sourceType = searchParams.get('sourceType') ?? '';
    const sourceId = searchParams.get('sourceId') ?? '';
    const phone = searchParams.get('phone') ?? '';
    const key = `${perfexLeadId}:${sourceType}:${sourceId}:${phone}`;
    if ((!perfexLeadId && (!sourceType || !sourceId) && !phone) || prefillKeyRef.current === key) return;
    prefillKeyRef.current = key;

    const e164 = normalizePhoneToE164(phone);
    if (e164) setNumber(e164);

    if (!perfexLeadId && (!sourceType || !sourceId)) return;

    const endpoint = sourceType && sourceId
      ? `/lead_context/source/${encodeURIComponent(sourceType)}/${encodeURIComponent(sourceId)}`
      : `/lead_context/${encodeURIComponent(perfexLeadId)}`;
    const qs = e164 && !sourceType ? `?phone=${encodeURIComponent(e164)}` : '';
    void api
      .get<{ lead: Lead }>(`${endpoint}${qs}`)
      .then((data) => {
        setLead(data.lead);
        setNumber(data.lead.phoneE164);
        setError(null);
      })
      .catch(() => {
        if (!e164) setError('Could not load lead context from Perfex');
      });
  }, [searchParams, setLead, setNumber]);

  const handleDigit = (d: string): void => {
    setNumber((number + d).slice(0, 17));
  };

  const handleDial = async (): Promise<void> => {
    setError(null);
    const e164 = normalizePhoneToE164(number);
    if (!e164 || !isValidUKPhoneNumber(e164)) {
      setError('Enter a valid UK number');
      return;
    }
    setBusy(true);
    try {
      // Single-line service: if a call is already up (agent opened the dialer
      // mid-call), hang it up and let its disconnect/disposition cycle settle
      // before placing the new one.
      if (softphoneService.isOnCall()) {
        await softphoneService.hangup(useSoftphone.getState().currentCall?.id).catch(() => {});
        await new Promise((resolve) => setTimeout(resolve, 400));
      }

      const lookup = await api
        .post<{ found: boolean; lead: Lead | null }>('/leads/lookup', { phone: e164 })
        .catch(() => ({ found: false, lead: null }));
      if (lookup.lead) setLead(lookup.lead);

      const result = await softphoneService.dial({
        toRaw: e164,
        leadId: lookup.lead?.id,
        callerId: selectedCallerId || undefined,
        callerProvider: selectedCallerProvider ?? undefined,
      });
      // The agent hit End call before this one connected. Deliberate, so don't
      // raise an error — setError would also flip the dialer to 'error' status.
      if (!result.ok && result.reason !== CANCELED_BEFORE_CONNECT) {
        setError(
          result.detail
            ?? result.reason
            ?? `Could not place call [dial-debug selected=${selectedCallerId || 'auto'} provider=${selectedCallerProvider ?? 'auto'} to=${e164}]`,
        );
      }
    } catch (err) {
      setError(extractApiErrorMessage(
        err,
        `Could not place call [dial-debug selected=${selectedCallerId || 'auto'} provider=${selectedCallerProvider ?? 'auto'} to=${e164}]`,
      ));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="label">Call from</label>
        <select
          className="input mb-3"
          value={selectedCallerId}
          onChange={(e) => {
            const value = e.target.value;
            const selectedNumber = numbersQuery.data?.find((n) => n.phoneE164 === value);
            setSelectedCallerId(value, value ? normalizeProvider(selectedNumber?.provider) : null);
          }}
        >
          <option value="">Auto (Local Presence)</option>
          {numbersQuery.data?.map((n) => (
            <option key={n.id} value={n.phoneE164}>
              {n.phoneE164} {n.label ? `(${n.label})` : ''} {n.provider === 'VOIPLINE' ? '[VP]' : ''}
            </option>
          ))}
        </select>
        <div className="mb-3 text-[11px] text-ink-500">
          Selected: <span className="font-mono">{selectedCallerId || 'Auto'}</span>
          {' '}
          <span className="font-semibold">{selectedCallerProvider ?? 'auto'}</span>
        </div>

        <label className="label">Call to</label>
        <div className="flex items-center gap-2">
          <input
            className="input min-w-0 flex-1 font-mono text-base sm:text-lg tracking-wider"
            placeholder="+44 ..."
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            autoComplete="off"
          />
          <button
            className="btn-secondary shrink-0"
            onClick={() => setNumber(number.slice(0, -1))}
            type="button"
            aria-label="Backspace"
          >
            ⌫
          </button>
        </div>
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      </div>

      <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
        {KEYS.map((key) => (
          <button
            key={key.digit}
            className="min-w-0 rounded-md border border-ink-200 bg-white py-2.5 sm:py-3 text-center transition hover:bg-ink-100"
            type="button"
            onClick={() => handleDigit(key.digit)}
          >
            <div className="text-lg sm:text-xl font-semibold leading-tight">{key.digit}</div>
            {key.letters && (
              <div className="text-[10px] uppercase tracking-wider text-ink-400 leading-tight">
                {key.letters}
              </div>
            )}
          </button>
        ))}
      </div>

      <button
        className="btn-primary w-full py-3 text-base"
        onClick={handleDial}
        disabled={busy || number.length < 4}
        type="button"
      >
        {busy ? 'Dialling…' : '📞 Call'}
      </button>
    </div>
  );
}
